import { MutationErrorResponse } from 'api/MutationErrorResponse';
import { AxiosHttpClient } from 'api/axios';
import { useRepruaDeleteApi, useRepruaIndexApi } from 'api/shared_api_hooks';
import { InstrumentImportResult } from 'entities/instrument_import_result';
import { BaseResponse, IndexApiSet } from 'rac';
import { useMutation } from 'react-query';
import { useMutationMessageHandler } from 'utils/hooks/use_mutation_message_handler';

type InstrumentImportResultsResponse = BaseResponse & {
  instrumentImportResults: InstrumentImportResult[];
};

type InstrumentImportResultsParams = {
  laboratoryId: number;
};

/**
 * ラボ側の機器のインポート結果の取得
 */
type LaboratoryInstrumentImportResultsApiSet = IndexApiSet<InstrumentImportResultsResponse> & {
  execute: () => void;
};
export const useLaboratoryInstrumentImportResults = (): LaboratoryInstrumentImportResultsApiSet => {
  const api = useRepruaIndexApi<InstrumentImportResultsResponse, InstrumentImportResultsParams>(
    new AxiosHttpClient('user'),
    {
      initialResponse: { instrumentImportResults: [] },
    }
  );

  const execute = () => {
    const path = 'instrument_import_results';
    api.execute(path);
  };

  return { ...api, execute };
};

type LaboratoryInstrumentImportResultResponse = BaseResponse & {
  instrumentImportResult: InstrumentImportResult;
};

type LaboratoryInstrumentImportResultParams = {
  uploadFile: File;
};

/**
 * ラボ側の機器のエクセルインポート
 */
export const usePostLaboratoryInstrumentImportMutation = () => {
  const client = new AxiosHttpClient('user');
  const mutation = useMutation<
    LaboratoryInstrumentImportResultResponse,
    MutationErrorResponse,
    LaboratoryInstrumentImportResultParams
  >('laboratories_set_instrument_import', async (params) => {
    const { data } = await client.post('instrument_import_results/excel', params);
    return data;
  });

  useMutationMessageHandler(mutation);

  return mutation;
};

/**
 * ラボ側の機器のインポートの取り消し
 */
export const useDeleteLaboratoryInstrumentImport = () => {
  const api = useRepruaDeleteApi<LaboratoryInstrumentImportResultResponse>(new AxiosHttpClient('user'), {
    initialResponse: { instrumentImportResult: {} },
  });

  const execute = (instrumentImportResultId: number) => {
    const path = `instrument_import_results/${instrumentImportResultId}`;
    api.execute(path);
  };

  return { ...api, execute };
};

/**
 * ラボ側のフォーマットのダウンロード
 */
export const useFetchLaboratoryInstrumentImportFormatMutation = () => {
  const client = new AxiosHttpClient('user');
  const mutation = useMutation<BaseResponse, MutationErrorResponse>(
    'laboratory_set_instrument_import',
    async (params) => {
      const { data } = await client.download('instrument_import_results/excel_format', params);
      return data;
    }
  );

  useMutationMessageHandler(mutation);

  return mutation;
};

type InstrumentSharingImportResultsResponse = BaseResponse & {
  instrumentImportResults: InstrumentImportResult[];
};

type InstrumentSharingImportResultsParams = {
  laboratoryId: number;
};

/**
 * ラボ側の共有機器のインポート結果の取得
 */
type LaboratoryInstrumentSharingImportResultsApiSet = IndexApiSet<InstrumentSharingImportResultsResponse> & {
  execute: () => void;
};
export const useLaboratoryInstrumentSharingImportResults = (): LaboratoryInstrumentSharingImportResultsApiSet => {
  const api = useRepruaIndexApi<InstrumentSharingImportResultsResponse, InstrumentSharingImportResultsParams>(
    new AxiosHttpClient('user'),
    {
      initialResponse: { instrumentImportResults: [] },
    }
  );

  const execute = () => {
    const path = 'instrument_sharing';
    api.execute(path);
  };

  return { ...api, execute };
};

type LaboratoryInstrumentSharingImportResultResponse = BaseResponse & {
  instrumentImportResult: InstrumentImportResult;
};

type LaboratoryInstrumentSharingImportResultParams = {
  dealerId?: string;
  uploadFile: File;
};

/**
 * ラボ側の共有機器のエクセルインポート
 */
export const usePostLaboratoryInstrumentSharingImportMutation = () => {
  const client = new AxiosHttpClient('user');
  const mutation = useMutation<
    LaboratoryInstrumentSharingImportResultResponse,
    MutationErrorResponse,
    LaboratoryInstrumentSharingImportResultParams
  >('laboratories_set_instrument_sharing_import', async (params) => {
    const { data } = await client.post('instrument_sharing/excel', params);
    return data;
  });

  useMutationMessageHandler(mutation);

  return mutation;
};

/**
 * ラボ側の共有機器のインポートの取り消し
 */
export const useDeleteLaboratoryInstrumentSharingImport = () => {
  const api = useRepruaDeleteApi<LaboratoryInstrumentSharingImportResultResponse>(new AxiosHttpClient('user'), {
    initialResponse: { instrumentImportResult: {} },
  });

  const execute = (instrumentImportResultId: number) => {
    const path = `instrument_sharing/${instrumentImportResultId}`;
    api.execute(path);
  };

  return { ...api, execute };
};
